import { useCallback, useEffect, useRef, useState } from 'react';
import { chooseEnrichment, fetchEnrichment, rejectEnrichment, reopenEnrichment } from '../api.js';
import { relativeTime } from '../../../shared/time.js';
import { useToast } from '../Toaster.jsx';

// Books the Library of Congress answered with records that do not carry their
// ISBN - other editions of the book, or other books sharing two words of its
// title and its author's surname - and books with no ISBN at all. A person
// says which record is the book, or that none is. See docs/loc-enrichment.md.
//
// A choice here is not one account's: the record is filed under the book, and
// every shelf holding it takes its genre and subjects from that record at its
// next sync. Which is why this is behind the site-admin role, and why the page
// says what was searched for - the reviewer is deciding for everybody.

const VIEWS = [
  { id: 'pending', label: 'Awaiting review' },
  { id: 'decided', label: 'Recently decided' },
  { id: 'notFound', label: 'Not found' },
];

// Who decided, in words. The enricher records the rule it chose by in place
// of an address - see loc.ChooseExact.
const AUTOMATIC = {
  'automatic: single exact match': 'Chosen automatically - the only record LoC has, with this title and author',
  'automatic: the book, over another format':
    'Chosen automatically - the book, where LoC also has it as an ebook or another format',
  'automatic: oldest of several exact matches':
    'Chosen automatically - the oldest of several records with this title and author',
};

const decidedBy = (who) =>
  AUTOMATIC[who] || (who?.startsWith('automatic') ? 'Chosen automatically' : who || 'Somebody');

// A record that is not a printed book says so, because the choice between an
// edition and its ebook is the one a reviewer most often has to make.
const FORMAT_TAG = { online: 'online', other: 'other format' };

const EMPTY = {
  pending: 'Nothing to review.',
  decided: 'Nothing decided in the last thirty days.',
  notFound: 'Every book searched for has been found.',
};

// The record's own page at LoC, when it has a control number LoC issued. A
// record LoC took in from elsewhere often has none, and there is nothing to
// link to.
const catalogueURL = (candidate) =>
  candidate.lccn ? `https://lccn.loc.gov/${encodeURIComponent(candidate.lccn)}` : null;

function Facts({ candidate }) {
  const facts = [candidate.edition, candidate.publisher, candidate.date, candidate.extent].filter(Boolean);
  return facts.length > 0 ? <p className="enrich-facts">{facts.join(' · ')}</p> : null;
}

// The page count in a record's extent - "xxix, 527 pages", "188 p." - or
// null for one that counts something else: "1 online resource", "2
// videodiscs". The first count of pages is the book's own; any after it are
// plates and unnumbered leaves.
function pageCount(extent) {
  const match = /(\d+)\s*(?:p\b|pages)/.exec(extent || '');
  return match ? Number(match[1]) : null;
}

// The columns, and what each sorts by. A record with nothing to sort by goes
// last whichever way the column is sorted, so that turning the order round
// does not bring the blanks to the top.
const COLUMNS = [
  { id: 'author', label: 'Author', value: (c) => c.author?.toLowerCase() || null },
  { id: 'title', label: 'Title', value: (c) => c.title?.toLowerCase() || null },
  { id: 'pages', label: 'Pages', numeric: true, value: (c) => pageCount(c.extent) },
  { id: 'publisher', label: 'Publisher', value: (c) => c.publisher?.toLowerCase() || null },
  // By the year the enricher read out of the date, since the date itself is
  // the catalogue's own text: "[2019]", "c1995", "©2005".
  { id: 'date', label: 'Published', numeric: true, value: (c) => c.year || null },
  { id: 'isbn', label: 'ISBN', value: (c) => c.isbns?.[0] || null },
];

const DEFAULT_SORT = { column: 'date', ascending: true };

// Ties keep LoC's own order, which is its ranking: sort is stable.
function sorted(candidates, { column, ascending }) {
  const value = COLUMNS.find((c) => c.id === column).value;
  return [...candidates].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (x === y) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return (x < y ? -1 : 1) * (ascending ? 1 : -1);
  });
}

// The ISBNs a record carries, linked to its page at LoC when it has one. A
// record with no ISBN still links, by its LCCN.
function ISBNs({ candidate }) {
  const url = catalogueURL(candidate);
  const text = candidate.isbns?.length > 0 ? candidate.isbns : candidate.lccn ? [`LCCN ${candidate.lccn}`] : [];
  if (text.length === 0) return <span className="enrich-blank">-</span>;
  const lines = text.map((line) => <span key={line}>{line}</span>);
  return url ? (
    <a className="inline-link enrich-isbns" href={url} target="_blank" rel="noreferrer" title="View at the Library of Congress">
      {lines}
    </a>
  ) : (
    <span className="enrich-isbns">{lines}</span>
  );
}

function Candidates({ candidates, chosen, sort, onSort, action }) {
  return (
    <div className="table-scroll enrich-table-scroll">
      <table className="admin-table enrich-table">
        <thead>
          <tr>
            {COLUMNS.map((column) => {
              const active = sort.column === column.id;
              return (
                <th
                  key={column.id}
                  className={column.numeric ? 'numeric' : undefined}
                  aria-sort={active ? (sort.ascending ? 'ascending' : 'descending') : 'none'}
                >
                  <button
                    type="button"
                    className={`enrich-sort${active ? ' active' : ''}`}
                    data-column={column.id}
                    onClick={() => onSort(column.id)}
                  >
                    {column.label}
                    <span className="enrich-sort-mark" aria-hidden="true">
                      {active ? (sort.ascending ? '▲' : '▼') : ''}
                    </span>
                  </button>
                </th>
              );
            })}
            {action && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {sorted(candidates, sort).map((candidate) => {
            const pages = pageCount(candidate.extent);
            const isChosen = candidate.id === chosen;
            return (
              <tr
                key={candidate.id}
                className={`enrich-candidate${isChosen ? ' enrich-candidate--chosen' : ''}`}
                data-record={candidate.id}
              >
                <td data-label="Author" className="enrich-wrap">
                  {candidate.author || <span className="enrich-blank">-</span>}
                </td>
                <td data-label="Title" className="enrich-wrap enrich-candidate-title enrich-title">
                  {candidate.title}
                  {isChosen && <span className="row-tag">chosen</span>}
                  {candidate.rejected && !isChosen && <span className="row-tag">rejected before</span>}
                  {FORMAT_TAG[candidate.format] && <span className="row-tag">{FORMAT_TAG[candidate.format]}</span>}
                  {candidate.edition && <span className="enrich-edition">{candidate.edition}</span>}
                </td>
                {/* The count where there is one, and the catalogue's own words
                    where there is not - "1 online resource" says something. */}
                <td data-label="Pages" className={pages === null ? 'enrich-wrap' : 'numeric'} title={candidate.extent || undefined}>
                  {pages ?? (candidate.extent || <span className="enrich-blank">-</span>)}
                </td>
                <td data-label="Publisher" className="enrich-wrap">
                  {candidate.publisher || <span className="enrich-blank">-</span>}
                </td>
                <td data-label="Published" className="numeric">
                  {candidate.date || <span className="enrich-blank">-</span>}
                </td>
                <td data-label="ISBN">
                  <ISBNs candidate={candidate} />
                </td>
                {action && (
                  // The flex box inside the cell rather than on it: a cell that
                  // is a flex box is no longer a table cell, and sits out of
                  // line with the rest of its row.
                  <td className="enrich-action">
                    <div className="row-actions">{action(candidate)}</div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Book({ item, children }) {
  return (
    <section className="panel enrich-book" data-key={item.key}>
      <div className="panel-header">
        <div>
          <h3 className="enrich-book-title">{item.title || 'Untitled'}</h3>
          <p className="panel-hint">
            {item.author || 'Unknown author'}
            {' · '}
            {item.isbn ? `ISBN ${item.isbn}` : 'no ISBN'}
          </p>
        </div>
      </div>
      <p className="enrich-query" title="What was asked of the Library of Congress">
        {item.query}
      </p>
      {children}
    </section>
  );
}

export default function EnrichmentPanel() {
  const notify = useToast();
  const [view, setView] = useState('pending');
  // The list together with the view it was fetched for. Switching views
  // changes `view` a render before the new list arrives, and drawing the old
  // list in the meantime drew the queue as though it were the decisions.
  const [loaded, setLoaded] = useState({ view: null, items: null });
  const items = loaded.view === view ? loaded.items : null;
  const [busy, setBusy] = useState('');
  // One order for every book on the page, so a reviewer who wants the
  // oldest edition first sorts once rather than once per book.
  const [sort, setSort] = useState(DEFAULT_SORT);
  const sortBy = (column) =>
    setSort((now) => ({ column, ascending: now.column === column ? !now.ascending : true }));
  // Which request the page is waiting on. Only its answer is drawn: a reload
  // after a decision can still be in flight when the view is switched, and
  // the queue arriving late would be drawn as the decided list.
  const latest = useRef(0);

  const reload = useCallback(() => {
    const asked = ++latest.current;
    fetchEnrichment(view)
      .then((list) => {
        if (asked === latest.current) setLoaded({ view, items: list });
      })
      .catch((error) => {
        if (asked !== latest.current) return;
        setLoaded({ view, items: [] });
        notify(`Could not load the review queue: ${error.message}`, { tone: 'error' });
      });
  }, [view]);

  useEffect(reload, [reload]);

  async function decide(item, what, run) {
    setBusy(item.key);
    try {
      await run();
      notify(`${item.title}: ${what}.`);
    } catch (error) {
      // 409 is somebody else, or a re-search, getting there first. The page
      // is stale either way, so it is reloaded rather than retried.
      notify(`Could not save that for ${item.title}: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
      reload();
    }
  }

  return (
    <>
      <section className="panel">
        <h2>Enrichment</h2>
        <p className="panel-hint">
          Books the Library of Congress has records for, none of them under the book's own
          ISBN. Pick the record that is the book and its genre and subjects reach every shelf
          holding it at that shelf's next sync - or say none is, and those records are not
          offered again.
        </p>
        <div className="enrich-views" role="tablist">
          {VIEWS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              className={view === id ? 'active' : ''}
              onClick={() => setView(id)}
            >
              {label}
              {id === view && items !== null && <span className="enrich-count">{items.length}</span>}
            </button>
          ))}
        </div>
      </section>

      {items === null ? null : items.length === 0 ? (
        <section className="panel">
          <p className="panel-empty">{EMPTY[view]}</p>
        </section>
      ) : view === 'pending' ? (
        items.map((item) => (
          <Book key={item.key} item={item}>
            <Candidates
              candidates={item.candidates}
              sort={sort}
              onSort={sortBy}
              action={(candidate) => (
                <button
                  type="button"
                  disabled={busy === item.key}
                  onClick={() => decide(item, 'chosen', () => chooseEnrichment(item.key, candidate.id))}
                >
                  This is the book
                </button>
              )}
            />
            <div className="row-actions enrich-none">
              <button
                type="button"
                className="danger"
                disabled={busy === item.key}
                onClick={() => decide(item, 'none of these', () => rejectEnrichment(item.key))}
              >
                None of these
              </button>
            </div>
          </Book>
        ))
      ) : view === 'notFound' ? (
        items.map((item) => (
          // Why it is a miss, which is one of two things: the search brought
          // nothing back, or a person said none of what it brought was the
          // book - and then what they turned down, and a way to take it back.
          // A miss is searched again a week after it was made either way.
          <Book key={item.key} item={item}>
            <div className="enrich-decision">
              {item.reviewedAt ? (
                <>
                  <p className="enrich-outcome">None of these.</p>
                  <p className="panel-hint">
                    {item.reviewedBy || 'Somebody'}, {relativeTime(item.reviewedAt)}
                  </p>
                  <Candidates candidates={item.candidates} chosen={item.chosen} sort={sort} onSort={sortBy} />
                  <div className="row-actions">
                    <button
                      type="button"
                      disabled={busy === item.key}
                      onClick={() => decide(item, 'reopened', () => reopenEnrichment(item.key))}
                    >
                      Reopen
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="enrich-outcome">The Library of Congress has no record for this search.</p>
                  <p className="panel-hint">Searched {relativeTime(item.checkedAt)}</p>
                </>
              )}
            </div>
          </Book>
        ))
      ) : (
        items.map((item) => {
          const chosen = item.candidates.find((candidate) => candidate.id === item.chosen);
          return (
            <Book key={item.key} item={item}>
              <div className="enrich-decision">
                <p className="enrich-outcome">
                  {chosen ? 'Chosen: ' : 'None of these.'}
                  {/* To the record's page at LoC, in a new tab so the list -
                      and the reviewer's place in it - stays where it was. A
                      record with no LCCN has no page there, and stays text. */}
                  {chosen && catalogueURL(chosen) ? (
                    <a className="inline-link" href={catalogueURL(chosen)} target="_blank" rel="noreferrer">
                      <strong>{chosen.title}</strong>
                    </a>
                  ) : (
                    chosen && <strong>{chosen.title}</strong>
                  )}
                </p>
                {chosen && <Facts candidate={chosen} />}
                <p className="panel-hint">
                  {decidedBy(item.reviewedBy)}
                  , {relativeTime(item.reviewedAt)}
                </p>
                <div className="row-actions">
                  <button
                    type="button"
                    disabled={busy === item.key}
                    onClick={() => decide(item, 'reopened', () => reopenEnrichment(item.key))}
                  >
                    Reopen
                  </button>
                </div>
              </div>
            </Book>
          );
        })
      )}
    </>
  );
}
